import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listClients, createClient, updateClient, deleteClient, getCompany } from "../lib/api";
import useCollapseMap from "../lib/useCollapseMap";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Mail, Phone, MapPin, Pencil, Trash2, Search, ChevronDown, ChevronRight, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import CountrySelect from "../components/CountrySelect";
import { TAX_REGIME_KEYS, TAX_REGIME_LABELS, TAX_REGIME_HINTS } from "../lib/taxRegimes";
import OverviewControls from "../components/overview/OverviewControls";
import { useOverviewPreferences } from "../hooks/useOverviewPreferences";
import { applySort, naturalCompare, statusOrderComparator } from "../lib/sortOverview";

// v14ah — Uitgebreide sorteeropties voor Klantenoverzicht.
const CLIENT_SORT_OPTIONS = [
  { value: "name_asc", label: "Naam A → Z" },
  { value: "name_desc", label: "Naam Z → A" },
  { value: "type_asc", label: "Type (Bedrijf eerst)" },
  { value: "location_asc", label: "Plaats A → Z" },
  { value: "location_desc", label: "Plaats Z → A" },
  { value: "created_desc", label: "Laatst toegevoegd" },
  { value: "updated_desc", label: "Laatst gewijzigd" },
];
// Business-order: bedrijf eerst (facturatie-relevant), daarna particulier.
const CLIENT_TYPE_ORDER = ["company", "private"];

// v14ah — Bepaal effectief weergave-adres per klant.
//   Bedrijf → facturatieadres (fallback: worksite_address).
//   Particulier → address.
// Gebruikt voor de "Adres" kolom én voor plaatssortering (eerste regel).
function pickAddress(c) {
  if (c?.type === "company") {
    return (c.billing_address || c.worksite_address || "").trim();
  }
  return (c?.address || "").trim();
}
// Eerste regel van het adres (na newline of komma) voor compacte weergave/sort.
function addressFirstLine(c) {
  const raw = pickAddress(c);
  if (!raw) return "";
  return raw.split(/\r?\n|,/)[0].trim();
}

// v14ai — Volledig adres: newlines → ", " zodat het één-regel wordt maar
// zo veel mogelijk info toont. Truncate valt terug in de UI-cel als het
// écht niet past. Whitespace wordt genormaliseerd.
function addressFull(c) {
  const raw = pickAddress(c);
  if (!raw) return "";
  return raw
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(", ")
    .replace(/\s+/g, " ")
    .trim();
}

const CLIENT_COMPARATORS = {
  name_asc: (a, b) => naturalCompare(a.name, b.name),
  name_desc: (a, b) => naturalCompare(b.name, a.name),
  type_asc: statusOrderComparator((c) => c.type || "private", CLIENT_TYPE_ORDER, "asc"),
  location_asc: (a, b) => naturalCompare(addressFirstLine(a), addressFirstLine(b)),
  location_desc: (a, b) => naturalCompare(addressFirstLine(b), addressFirstLine(a)),
  created_desc: (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")),
  updated_desc: (a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")),
};

// v6 F14 §4 — Country blijft optioneel in de state; wordt bij openNew()
// geprefilled met company.country (default "BE" als bedrijf niets heeft).
const empty = {
  type: "private", name: "", company_name: "", vat_number: "",
  contact_person: "", contact_role: "",
  address: "", billing_address: "", worksite_address: "",
  country: "",
  email: "", phone: "", notes: "",
  // v6 F3.5b — Fiscaal regime van de klant (erft naar factuur).
  default_tax_regime_key: "standard",
};

// v14ah — Type-badge: visueel onderscheid Particulier vs Bedrijf.
// Particulier → neutraal grijs (rustig), Bedrijf → terracotta-accent (identity).
function ClientTypeBadge({ type }) {
  const isCompany = type === "company";
  const cls = isCompany
    ? "bg-[#FFF5EF] text-[#D96C4A] border-[#D96C4A]"
    : "bg-[#F0EFEA] text-[#4F4F4D] border-[#E5E2DC]";
  const Icon = isCompany ? Building2 : User;
  const label = isCompany ? "Bedrijf" : "Particulier";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${cls}`}
      data-testid={`client-type-badge-${type || "private"}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

// v14ai — Compacte landcode-badge. Toon enkel wanneer:
//   • klantland gezet is,
//   • én het verschilt van het bedrijfsland (bv. NL-klant in BE-bureau).
// Voor gelijkland blijft het overzicht rustig zonder redundante badge.
function CountryBadge({ country, companyCountry }) {
  const cc = (country || "").toUpperCase();
  const home = (companyCountry || "").toUpperCase();
  if (!cc || cc === home) return null;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#F0EFEA] text-[#4F4F4D] border border-[#E5E2DC]"
      title={`Land: ${cc}`}
      data-testid={`country-badge-${cc}`}
    >
      {cc}
    </span>
  );
}

// v14ai — Één rij in de Klantenlijst.
// Desktop (lg+): 7-koloms grid (Type | Klant/Bedrijf | Adres | BTW-nr |
// E-mail | Telefoon | Acties). Contactpersoon is bewust NIET meer een aparte
// kolom (leeft in de klantfiche/kaartweergave). Adres krijgt maximale ruimte.
function ClientListRow({ client: c, companyCountry, onEdit, onDelete }) {
  const isCompany = c.type === "company";
  const primaryName = c.name || "—";
  const address = addressFull(c);
  const vat = c.vat_number || "—";
  const email = c.email || "";
  const phone = c.phone || "";
  return (
    <li className="hover:bg-[#FAF9F6]" data-testid={`client-row-${c.id}`}>
      {/* Desktop grid row */}
      <div
        className="hidden lg:grid gap-3 px-4 py-3 items-center text-sm"
        style={{ gridTemplateColumns: "96px minmax(0,1.2fr) minmax(0,2fr) 130px minmax(0,1fr) 120px 88px" }}
      >
        <div data-testid={`row-type-${c.id}`}>
          <ClientTypeBadge type={c.type} />
        </div>
        <div className="min-w-0 truncate text-[#2C2C2A] font-medium" title={primaryName} data-testid={`row-name-${c.id}`}>
          {primaryName}
        </div>
        <div className="min-w-0 flex items-center gap-2" data-testid={`row-address-${c.id}`}>
          <span className="min-w-0 truncate text-[#4F4F4D]" title={address}>
            {address || "—"}
          </span>
          <CountryBadge country={c.country} companyCountry={companyCountry} />
        </div>
        <div className="min-w-0 truncate text-[#4F4F4D] font-mono text-xs" title={vat} data-testid={`row-vat-${c.id}`}>
          {vat}
        </div>
        <div className="min-w-0 truncate" data-testid={`row-email-${c.id}`}>
          {email ? (
            <a href={`mailto:${email}`} className="text-[#2C2C2A] hover:text-[#D96C4A] truncate block" title={email}>
              {email}
            </a>
          ) : (
            <span className="text-[#A8A7A0]">—</span>
          )}
        </div>
        <div className="min-w-0 truncate" data-testid={`row-phone-${c.id}`}>
          {phone ? (
            <a href={`tel:${phone}`} className="text-[#2C2C2A] hover:text-[#D96C4A] tabular-nums" title={phone}>
              {phone}
            </a>
          ) : (
            <span className="text-[#A8A7A0]">—</span>
          )}
        </div>
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={onEdit} data-testid={`edit-client-${c.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-[#B84B45]" onClick={onDelete} data-testid={`delete-client-${c.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile / tablet compacte layout (< lg) */}
      <div className="lg:hidden px-4 py-3 space-y-2" data-testid={`client-row-mobile-${c.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <ClientTypeBadge type={c.type} />
              <CountryBadge country={c.country} companyCountry={companyCountry} />
            </div>
            <div className="text-[#2C2C2A] font-semibold truncate" title={primaryName} data-testid={`row-name-mobile-${c.id}`}>
              {primaryName}
            </div>
            {isCompany && c.contact_person && (
              <div className="text-xs text-[#4F4F4D] truncate" data-testid={`row-contact-mobile-${c.id}`}>
                {c.contact_person}{c.contact_role ? ` · ${c.contact_role}` : ""}
              </div>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={onEdit} data-testid={`edit-client-mobile-${c.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-[#B84B45]" onClick={onDelete} data-testid={`delete-client-mobile-${c.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-[#4F4F4D]">
          {address && (
            <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{address}</span>
            </span>
          )}
          {isCompany && c.vat_number && (
            <span className="font-mono text-[11px]" data-testid={`row-vat-mobile-${c.id}`}>{c.vat_number}</span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap text-xs">
          {email ? (
            <a href={`mailto:${email}`} className="inline-flex items-center gap-1 text-[#2C2C2A] hover:text-[#D96C4A] min-w-0 max-w-full">
              <Mail className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{email}</span>
            </a>
          ) : null}
          {phone ? (
            <a href={`tel:${phone}`} className="inline-flex items-center gap-1 text-[#2C2C2A] hover:text-[#D96C4A]">
              <Phone className="w-3.5 h-3.5" />
              <span className="tabular-nums">{phone}</span>
            </a>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export default function Clients() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  // v6 F14 §4 — Bedrijfsland ophalen om nieuwe klanten daarmee te prefillen.
  // Fallback: "BE" wanneer het bedrijf nog geen land heeft ingesteld.
  const [companyCountry, setCompanyCountry] = useState("BE");

  const refresh = () => listClients().then(setItems).catch(() => {});

  useEffect(() => { refresh(); }, []);
  // v14ah — Auto-refresh bij page focus / visibility return.
  // Vangt: gebruiker wijzigt klant in andere tab of workflow → lijst is stale.
  useEffect(() => {
    const onFocus = () => { refresh(); };
    const onVisibility = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  useEffect(() => {
    getCompany()
      .then((c) => {
        const cc = (c?.country || "").toString().toUpperCase();
        if (cc) setCompanyCountry(cc);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (params.get("new")) {
      openNew();
      params.delete("new");
      setParams(params, { replace: true });
    }
    const editId = params.get("edit");
    if (editId) {
      const c = items.find((x) => x.id === editId);
      if (c) openEdit(c);
      params.delete("edit");
      setParams(params, { replace: true });
    }
  }, [items, params]); // eslint-disable-line

  const openNew = () => {
    setEditing(null);
    // v6 F14 §4 — Prefill land met het bedrijfsland zodat een BE-schilder
    // niet elk klant handmatig op "België" moet zetten. Gebruiker kan altijd
    // wijzigen (naar bv. Spanje voor een tweede-verblijf-project).
    setForm({ ...empty, country: companyCountry || "BE" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, country: (c.country || "").toUpperCase() });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() && !form.company_name?.trim()) {
      toast.error("Naam is verplicht");
      return;
    }
    const payload = { ...form };
    // If company and only company_name is filled, use it as the main name
    if (payload.type === "company" && !payload.name?.trim() && payload.company_name?.trim()) {
      payload.name = payload.company_name.trim();
    }
    delete payload.company_name; // not in backend schema
    try {
      if (editing) {
        await updateClient(editing.id, payload);
        toast.success("Klant bijgewerkt");
      } else {
        await createClient(payload);
        toast.success("Klant toegevoegd");
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error("Fout bij opslaan");
    }
  };

  const remove = async (id) => {
    try {
      await deleteClient(id);
      toast.success("Klant verwijderd");
      setConfirmDel(null);
      refresh();
    } catch {
      toast.error("Fout bij verwijderen");
    }
  };

  // v14ah — Uitgebreide zoek: naam, e-mail, adres, bedrijfsnaam,
  // BTW-nummer, telefoon, contactpersoon (case-insensitive).
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      const hay = [
        c.name, c.email, c.address, c.billing_address, c.worksite_address,
        c.company_name, c.vat_number, c.phone, c.contact_person, c.contact_role,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  // v14ad Fase 1 — Persistente view + sort per module.
  const { view, setView, sort, setSort } = useOverviewPreferences("clients", "cards", "name_asc");
  const filtered = useMemo(() => applySort(searched, sort, CLIENT_COMPARATORS), [searched, sort]);

  // v6 F3 — Persisted accordion state per client. Default = collapsed.
  const filteredIds = useMemo(() => filtered.map((c) => ({ id: c.id })), [filtered]);
  const {
    isCollapsed: isClientCollapsed, toggle: toggleClient,
    expandAll: expandAllClients, collapseAll: collapseAllClients,
  } = useCollapseMap("emma:listCollapsed:clients", filteredIds, true);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] leading-tight">Klanten</h1>
          <p className="text-[#4F4F4D] mt-1">Beheer je klantenbestand voor offertes en projecten.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-3 sm:p-4">
        <OverviewControls
          moduleKey="clients"
          search={{ value: search, onChange: setSearch, placeholder: "Zoek klant…", testId: "client-search-input" }}
          sort={{ value: sort, onChange: setSort, options: CLIENT_SORT_OPTIONS }}
          view={{ value: view, onChange: setView }}
          actions={
            <Button
              onClick={openNew}
              className="h-11 px-4 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white gap-2 flex-shrink-0 w-full sm:w-auto"
              data-testid="new-client-button"
            >
              <Plus className="w-4 h-4" /> <span className="whitespace-nowrap">Nieuwe klant</span>
            </Button>
          }
        />
      </div>

      {view === "list" ? (
        <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="clients-list-view">
          {filtered.length === 0 ? (
            <div className="text-center py-8 space-y-1.5">
              <p className="text-sm text-[#4F4F4D]">Nog geen klanten.</p>
              <p className="text-xs text-[#A8A7A0]">Klik op <strong>Nieuwe klant</strong> om te starten.</p>
            </div>
          ) : (
            <>
              {/* v14ai — Desktop kolomkop (7 kolommen, Contactpersoon weg;
                  Adres krijgt de vrijgekomen breedte). */}
              <div
                className="hidden lg:grid gap-3 px-4 py-2 bg-[#FAF9F6] border-b border-[#E5E2DC] text-[11px] font-semibold uppercase tracking-wide text-[#6E6E6C]"
                style={{ gridTemplateColumns: "96px minmax(0,1.2fr) minmax(0,2fr) 130px minmax(0,1fr) 120px 88px" }}
                data-testid="clients-list-header"
              >
                <div>Type</div>
                <div>Klant / Bedrijf</div>
                <div>Adres</div>
                <div>BTW-nr</div>
                <div>E-mail</div>
                <div>Telefoon</div>
                <div className="text-right">Acties</div>
              </div>
              <ul className="divide-y divide-[#E5E2DC]">
                {filtered.map((c) => (
                  <ClientListRow
                    key={c.id}
                    client={c}
                    companyCountry={companyCountry}
                    onEdit={() => openEdit(c)}
                    onDelete={() => setConfirmDel(c)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="clients-grid">
        {filtered.length === 0 && (
          <div className="emma-card md:col-span-2 xl:col-span-3 text-center py-8 space-y-1.5">
            <p className="text-sm text-[#4F4F4D]">Nog geen klanten.</p>
            <p className="text-xs text-[#A8A7A0]">Klik op <strong>Nieuwe klant</strong> om te starten.</p>
          </div>
        )}
        {filtered.length > 1 && (
          <div className="md:col-span-2 xl:col-span-3 flex items-center justify-end gap-2 -mb-2" data-testid="clients-bulk-toolbar">
            <button onClick={expandAllClients} className="text-xs text-[#D96C4A] hover:underline px-2 py-1" data-testid="expand-all-clients">Alles open</button>
            <span className="text-xs text-[#A8A7A0]">·</span>
            <button onClick={collapseAllClients} className="text-xs text-[#4F4F4D] hover:underline px-2 py-1" data-testid="collapse-all-clients">Alles dicht</button>
          </div>
        )}
        {filtered.map((c) => {
          const collapsed = isClientCollapsed(c.id);
          return (
          <div key={c.id} className="emma-card group" data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                onClick={() => toggleClient(c.id)}
                className="flex-1 min-w-0 text-left -m-1 p-1 rounded-lg hover:bg-[#FAF9F6]"
                data-testid={`client-toggle-${c.id}`}
                aria-expanded={!collapsed}
              >
                <div className="flex items-start gap-2">
                  {collapsed
                    ? <ChevronRight className="w-4 h-4 mt-1 text-[#5D7259] flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 mt-1 text-[#5D7259] flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1">
                      <ClientTypeBadge type={c.type} />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-[#2C2C2A] truncate">{c.name}</h3>
                    {c.type === "company" && c.contact_person && (
                      <div className="text-xs text-[#4F4F4D] truncate mt-0.5">
                        {c.contact_person}{c.contact_role ? ` · ${c.contact_role}` : ""}
                      </div>
                    )}
                    {addressFirstLine(c) && (
                      <div className="text-sm text-[#4F4F4D] flex items-center gap-1.5 mt-1">
                        <MapPin className="w-3.5 h-3.5" /> <span className="truncate">{addressFirstLine(c)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
              <div className="flex gap-1 transition">
                <button
                  onClick={() => openEdit(c)}
                  className="w-9 h-9 rounded-lg hover:bg-[#F0EFEA] flex items-center justify-center"
                  data-testid={`edit-client-${c.id}`}
                >
                  <Pencil className="w-4 h-4 text-[#4F4F4D]" />
                </button>
                <button
                  onClick={() => setConfirmDel(c)}
                  className="w-9 h-9 rounded-lg hover:bg-red-50 flex items-center justify-center"
                  data-testid={`delete-client-${c.id}`}
                >
                  <Trash2 className="w-4 h-4 text-[#D9534F]" />
                </button>
              </div>
            </div>
            {!collapsed && (
              <>
                <div className="mt-3 space-y-1.5 text-sm">
                  {c.type === "company" && c.vat_number && (
                    <div className="flex items-center gap-1.5 text-[#4F4F4D] font-mono text-xs" data-testid={`card-vat-${c.id}`}>
                      <span className="text-[#A8A7A0] uppercase tracking-wide text-[10px] font-sans">BTW-nr</span> {c.vat_number}
                    </div>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-[#2C2C2A] hover:text-[#D96C4A] truncate">
                      <Mail className="w-3.5 h-3.5" /> {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-[#2C2C2A] hover:text-[#D96C4A]">
                      <Phone className="w-3.5 h-3.5" /> {c.phone}
                    </a>
                  )}
                </div>
                {c.notes && (
                  <p className="text-sm text-[#4F4F4D] mt-3 line-clamp-2 border-t border-[#E5E2DC] pt-3">{c.notes}</p>
                )}
              </>
            )}
          </div>
          );
        })}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-2rem)] sm:w-full p-0 max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col gap-0 overflow-hidden"
          data-testid="client-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E5E2DC]">
            <DialogTitle className="font-heading">
              {editing ? "Klant bewerken" : "Nieuwe klant"}
            </DialogTitle>
            <DialogDescription>Vul de gegevens van de klant in.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4" data-testid="client-dialog-body">
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={form.type === "private" ? "default" : "outline"}
                className={`h-11 rounded-xl ${form.type === "private" ? "bg-[#D96C4A] hover:bg-[#C05A3C]" : ""}`}
                onClick={() => setForm({ ...form, type: "private" })}
                data-testid="client-type-private"
              >
                Particulier
              </Button>
              <Button
                type="button"
                variant={form.type === "company" ? "default" : "outline"}
                className={`h-11 rounded-xl ${form.type === "company" ? "bg-[#D96C4A] hover:bg-[#C05A3C]" : ""}`}
                onClick={() => setForm({ ...form, type: "company" })}
                data-testid="client-type-company"
              >
                Bedrijf
              </Button>
            </div>

            {form.type === "company" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Bedrijfsnaam *</Label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="h-12 mt-1.5 rounded-xl" data-testid="client-company-input" />
                </div>
                <div>
                  <Label>BTW-nummer</Label>
                  <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                    className="h-12 mt-1.5 rounded-xl font-mono" placeholder="BE 0XXX XXX XXX" data-testid="client-vat-input" />
                </div>
                <div>
                  <Label>Functie</Label>
                  <Input value={form.contact_role} onChange={(e) => setForm({ ...form, contact_role: e.target.value })}
                    className="h-12 mt-1.5 rounded-xl" placeholder="bv. zaakvoerder" data-testid="client-role-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Contactpersoon</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                    className="h-12 mt-1.5 rounded-xl" data-testid="client-contact-input" />
                </div>
              </div>
            )}

            <div>
              <Label>{form.type === "company" ? "Naam (referentie)" : "Naam"} *</Label>
              <Input id="cname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12 mt-1.5 rounded-xl" data-testid="client-name-input" />
            </div>

            {form.type === "company" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Facturatieadres</Label>
                  <Textarea value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                    rows={2} className="mt-1.5 rounded-xl" data-testid="client-billing-input" />
                </div>
                <div>
                  <Label>Werfadres</Label>
                  <Textarea value={form.worksite_address} onChange={(e) => setForm({ ...form, worksite_address: e.target.value })}
                    rows={2} className="mt-1.5 rounded-xl" data-testid="client-worksite-input" />
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="caddr">Adres</Label>
                <Textarea id="caddr" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2} className="mt-1.5 rounded-xl" data-testid="client-address-input" />
              </div>
            )}

            {/* v6 F14 §4 — Land als apart adresveld. Prefill = bedrijfsland,
                aanpasbaar via doorzoekbare landkeuze. Toont ook een hint dat
                het pas op de PDF verschijnt als het van het eigen bedrijfsland
                afwijkt (dat compact adres opvangt bij binnenlandse klanten). */}
            <div>
              <Label htmlFor="ccountry">Land</Label>
              <CountrySelect
                value={form.country}
                onChange={(code) => setForm({ ...form, country: code })}
                placeholder={`Standaard: ${companyCountry}`}
                testId="client-country-select"
              />
              <p className="text-[11px] text-[#6E6E6C] mt-1 leading-snug">
                Wordt op de offerte-PDF getoond wanneer het verschilt van je eigen bedrijfsland
                {companyCountry ? ` (${companyCountry}).` : "."}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cmail">E-mail</Label>
                <Input id="cmail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="client-email-input" />
              </div>
              <div>
                <Label htmlFor="cphone">Telefoon</Label>
                <Input id="cphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="client-phone-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="cnotes">Notities</Label>
              <Textarea id="cnotes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} className="mt-1.5 rounded-xl" data-testid="client-notes-input" />
            </div>

            {/* v6 F3.5b — Fiscaal regime van de klant. Erft naar facturen. */}
            <div>
              <Label htmlFor="ctax">Standaard BTW-regime</Label>
              <select
                id="ctax"
                value={form.default_tax_regime_key || "standard"}
                onChange={(e) => setForm({ ...form, default_tax_regime_key: e.target.value })}
                className="mt-1.5 flex h-12 w-full rounded-xl border border-[#E5E2DC] bg-white px-3 text-sm"
                data-testid="client-tax-regime-select"
              >
                {TAX_REGIME_KEYS.map((k) => (
                  <option key={k} value={k}>{TAX_REGIME_LABELS[k]}</option>
                ))}
              </select>
              <p className="text-[11px] text-[#6E6E6C] mt-1 leading-snug">
                {TAX_REGIME_HINTS[form.default_tax_regime_key || "standard"]}
              </p>
            </div>
          </div>
          <DialogFooter
            className="px-5 py-3 border-t border-[#E5E2DC] bg-white flex-row gap-2 sm:flex-row"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12 rounded-xl flex-1 sm:flex-none" data-testid="client-cancel-btn">
              Annuleer
            </Button>
            <Button onClick={save} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] flex-1 sm:flex-none" data-testid="client-save-btn">
              {editing ? "Bijwerken" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent data-testid="delete-client-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Klant verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDel?.name}" en alle gekoppelde projecten worden verwijderd. Deze actie is onomkeerbaar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-client-cancel">Annuleer</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#D9534F] hover:bg-[#B23A3A]"
              onClick={() => remove(confirmDel.id)}
              data-testid="delete-client-confirm-btn"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
